// Staff access rules: a session is scoped to one venue, and PIN sign-in is rate limited. Pure functions, no I/O.
// The API applies them per device (cookie) and per IP; both counters must be clear for a PIN to be checked.
import { UI, fill } from './copy';
import { quotaLevel, showSmsQuota, type QuotaLevel } from './notify';
import { TIMERS, type Lang } from './ticket';

export const PIN_LOCKOUT = {
  /** Failed attempts allowed inside one window before the lock. */
  maxFailures: 5,
  /** Failures older than this no longer count. */
  windowMs: 15 * 60_000,
  /** How long the 5th failure locks sign-in. */
  lockMs: 15 * 60_000,
} as const;

export interface PinAttempts {
  failures: number;
  /** Time of the first failure of the current window; 0 when there is none. */
  windowStart: number;
  lockedUntil: number | null;
}

export const NO_PIN_ATTEMPTS: PinAttempts = { failures: 0, windowStart: 0, lockedUntil: null };

function windowExpired(a: PinAttempts, now: number): boolean {
  return a.failures === 0 || now - a.windowStart >= PIN_LOCKOUT.windowMs;
}

/** Checked before the PIN itself: a locked key answers 429 without comparing anything. */
export function pinLockStatus(a: PinAttempts, now: number): { locked: false } | { locked: true; retryAfterMs: number } {
  if (a.lockedUntil !== null && now < a.lockedUntil) return { locked: true, retryAfterMs: a.lockedUntil - now };
  return { locked: false };
}

/** The worst of several counters (device and IP): locked if any is locked, with the longest wait. */
export function combinedLockStatus(all: readonly PinAttempts[], now: number): { locked: false } | { locked: true; retryAfterMs: number } {
  let retryAfterMs = 0;
  for (const a of all) {
    const s = pinLockStatus(a, now);
    if (s.locked) retryAfterMs = Math.max(retryAfterMs, s.retryAfterMs);
  }
  return retryAfterMs > 0 ? { locked: true, retryAfterMs } : { locked: false };
}

/** A wrong PIN: failures inside the window add up; the fifth one locks for 15 minutes. An expired window starts over. */
export function recordPinFailure(a: PinAttempts, now: number): PinAttempts {
  const fresh = windowExpired(a, now);
  const failures = fresh ? 1 : a.failures + 1;
  const windowStart = fresh ? now : a.windowStart;
  const lockedUntil = failures >= PIN_LOCKOUT.maxFailures ? now + PIN_LOCKOUT.lockMs : null;
  return { failures, windowStart, lockedUntil };
}

/** Attempts still allowed in this window, for the "wrong PIN" message. */
export function pinAttemptsLeft(a: PinAttempts, now: number): number {
  if (windowExpired(a, now)) return PIN_LOCKOUT.maxFailures;
  return Math.max(0, PIN_LOCKOUT.maxFailures - a.failures);
}

/** Whole minutes to wait, never below 1, so the message never says "0 min". */
export function retryMinutes(retryAfterMs: number): number {
  return Math.max(1, Math.ceil(retryAfterMs / 60_000));
}

export function pinLockedMessage(lang: Lang, retryAfterMs: number): string {
  return fill(UI[lang].pin_locked, { minutes: String(retryMinutes(retryAfterMs)) });
}

export function wrongPinMessage(lang: Lang, attemptsLeft: number): string {
  return fill(UI[lang].pin_wrong, { attempts_left: String(attemptsLeft) });
}

// ---- scope ----

export interface StaffSession {
  venueId: string;
}

/** A pickup point exists for a staff session only inside the session's venue; anything else is "not found". */
export function staffCanSeePickupPoint(session: StaffSession, pp: { venueId: string }): boolean {
  return session.venueId === pp.venueId;
}

/** A ticket can be acted on only through its own pickup point, which must itself belong to the session's venue. */
export function staffCanActOnTicket(session: StaffSession, pp: { id: string; venueId: string }, ticket: { pickupPointId: string; venueId: string }): boolean {
  return staffCanSeePickupPoint(session, pp) && ticket.pickupPointId === pp.id && ticket.venueId === pp.venueId;
}

// ---- staff screen rows and header (issue #14) ----

/** Whole minutes since the ticket was issued, never negative: "3 min" means at least three full minutes. */
export function minutesWaiting(createdAt: number, now: number): number {
  return Math.max(0, Math.floor((now - createdAt) / 60_000));
}

/** What the Renvoyer button does right now: enabled, counting down, or spent. `left` is how many resends remain. */
export type ResendAvailability = { kind: 'available'; left: number } | { kind: 'cooldown'; secondsLeft: number; left: number } | { kind: 'exhausted' };

export function resendAvailability(t: { resends: number; lastResendAt: number | null }, now: number): ResendAvailability {
  const left = Math.max(0, TIMERS.maxResends - t.resends);
  if (left === 0) return { kind: 'exhausted' };
  const cooldownEnds = t.lastResendAt === null ? null : t.lastResendAt + TIMERS.resendCooldownMs;
  if (cooldownEnds === null || now >= cooldownEnds) return { kind: 'available', left };
  return { kind: 'cooldown', secondsLeft: Math.max(1, Math.ceil((cooldownEnds - now) / 1000)), left };
}

/** The Renvoyer label for that availability, from the copy table. */
export function resendLabel(lang: Lang, a: ResendAvailability): string {
  const t = UI[lang];
  switch (a.kind) {
    case 'available':
      return t.resend;
    case 'cooldown':
      return fill(t.resend_countdown, { seconds: String(a.secondsLeft) });
    case 'exhausted':
      return t.resend_exhausted;
  }
}

/**
 * The header counter: hidden under 50 % of the quota, then "SMS utilisés : 52 / 100", with a word at 80 % and at
 * 100 %. `label` is the whole line; `note` is that word alone (null below 80 %), for a screen that renders the
 * count from `sms_used` itself so the digits can sit in an LTR run.
 */
export function smsQuotaCopy(lang: Lang, used: number, quota: number): { symbol: string; label: string; level: QuotaLevel; note: string | null } | null {
  if (!showSmsQuota(used, quota)) return null;
  const t = UI[lang];
  const level = quotaLevel(used, quota);
  const count = fill(t.sms_used, { used: String(used), quota: String(quota) });
  if (level === 'exhausted') return { symbol: '✕', label: `${count} · ${t.sms_quota_exhausted}`, level, note: t.sms_quota_exhausted };
  if (level === 'warning') return { symbol: '⚠', label: `${count} · ${t.sms_quota_warning}`, level, note: t.sms_quota_warning };
  return { symbol: '✉', label: count, level, note: null };
}

// ---- correcting a wrong number (issue #17) ----

/** The inline edit line under a waiting row: the prompt names the current number so staff see which order they are changing. */
export function editNumberCopy(lang: Lang, displayNo: string): { label: string; prompt: string; save: string; cancel: string } {
  const t = UI[lang];
  return { label: t.edit_number, prompt: fill(t.edit_number_prompt, { order_number: displayNo }), save: t.save_number, cancel: t.edit_number_cancel };
}

/**
 * What the edit line and the Ajouter form (issue #59) say when the server refuses the number, in the screen language.
 * A duplicate gets the same hint as the diner's receipt form (`number_open`), a malformed number the same 1-to-6-digits
 * line (`bad_number`); null for any other code, so the caller falls back to the API label.
 */
export function numberErrorCopy(lang: Lang, code: string | undefined): string | null {
  const t = UI[lang];
  switch (code) {
    case 'number_open':
      return t.number_open;
    case 'bad_number':
      return t.bad_number;
    case 'not_waiting':
      return t.number_locked;
    default:
      return null;
  }
}

// ---- staff always type the order number (owner request, issue #102) ----

/** The labelled staff number field and add action, independent of the venue's guest registration mode. */
export function addTicketCopy(lang: Lang): { button: string; field: string } {
  const t = UI[lang];
  return { button: t.add_receipt, field: t.receipt_no_label };
}

/** The cancel confirmation, with the number in the question so staff see which row they are about to close. */
export function cancelConfirmCopy(lang: Lang, displayNo: string): { question: string; confirm: string; keep: string } {
  const t = UI[lang];
  return { question: fill(t.cancel_confirm, { order_number: displayNo }), confirm: t.confirm_cancel, keep: t.keep_order };
}

// ---- kitchen view and pickup point filter (issue #18) ----

/**
 * The two ways to look at the staff screen. The counter does everything (issue numbers, Prêt, Retirée, Renvoyer,
 * cancel); the kitchen only cooks and calls, so its view keeps the open orders and the Prêt button and nothing else.
 */
export type StaffView = 'counter' | 'kitchen';

/** The view a device stored; anything but "kitchen" is the counter view. */
export function parseStaffView(raw: string | null | undefined): StaffView {
  return raw === 'kitchen' ? 'kitchen' : 'counter';
}

/** The filter value that shows every pickup point of the venue at once. */
export const ALL_PICKUP_POINTS = 'all';

/**
 * Which pickup point(s) a device watches: `ALL_PICKUP_POINTS` or one pickup point id. A stored value is trusted only
 * while it names one of the venue's pickup points (a counter removed in the settings falls back to the link's);
 * a venue with a single pickup point always shows that one. `fallback` is the pickup point of the link the screen
 * was opened with.
 */
export function resolveStaffFilter(raw: string | null | undefined, pickupPointIds: readonly string[], fallback: string): string {
  if (pickupPointIds.length <= 1) return pickupPointIds[0] ?? fallback;
  if (raw === ALL_PICKUP_POINTS) return ALL_PICKUP_POINTS;
  if (raw && pickupPointIds.includes(raw)) return raw;
  return fallback;
}

export interface OpenCounts {
  waiting: number;
  ready: number;
  /** waiting + ready: the count the filter shows next to each pickup point. */
  open: number;
}

/** Open orders of one board (none when the board has not arrived yet). */
export function openCounts(board: { waiting: readonly unknown[]; ready: readonly unknown[] } | null | undefined): OpenCounts {
  const waiting = board?.waiting.length ?? 0;
  const ready = board?.ready.length ?? 0;
  return { waiting, ready, open: waiting + ready };
}

/** Only open orders are listed, in either view: a collected, cancelled or expired row never stays on the screen. */
export function staffShows(status: string): boolean {
  return status === 'waiting' || status === 'ready';
}

export type StaffAction = 'ready' | 'collected' | 'resend' | 'cancel';

/**
 * The buttons a row offers. The counter view keeps them all; the kitchen view is Prêt only: a waiting order gets
 * that one button, a ready order none (the counter marks it collected).
 */
export function rowActions(view: StaffView, status: string): StaffAction[] {
  if (!staffShows(status)) return [];
  if (view === 'kitchen') return status === 'waiting' ? ['ready'] : [];
  return status === 'waiting' ? ['ready', 'cancel'] : ['collected', 'resend', 'cancel'];
}

interface StaffRowLike {
  status: string;
  createdAt: number;
  readyAt: number | null;
}

/**
 * The rows the screen lists, each tagged with its pickup point. The counter view is one list, newest at the bottom
 * (US-30), across the selected pickup points. The kitchen view lists what is left to cook first (waiting, oldest at
 * the top), then what sits at the pass (ready, oldest first), so the next plate to make is always at the top.
 */
export function staffRows<T extends StaffRowLike>(
  boards: Readonly<Record<string, { waiting: readonly T[]; ready: readonly T[] } | undefined>>,
  filter: string,
  view: StaffView,
): (T & { pickupPointId: string })[] {
  const ids = filter === ALL_PICKUP_POINTS ? Object.keys(boards) : [filter];
  const rows: (T & { pickupPointId: string })[] = [];
  for (const pickupPointId of ids) {
    const b = boards[pickupPointId];
    if (!b) continue;
    for (const o of [...b.waiting, ...b.ready]) if (staffShows(o.status)) rows.push({ ...o, pickupPointId });
  }
  if (view === 'kitchen') {
    const waiting = rows.filter((o) => o.status === 'waiting').sort((a, b) => a.createdAt - b.createdAt);
    const ready = rows.filter((o) => o.status === 'ready').sort((a, b) => (a.readyAt ?? a.createdAt) - (b.readyAt ?? b.createdAt));
    return [...waiting, ...ready];
  }
  return rows.sort((a, b) => a.createdAt - b.createdAt);
}
